import { Avatar, Heading, UserWithCompanyAvatar } from 'copilot-design-system';
import React from 'react';

import { CompanyFields, UserFields } from 'src/constants';
import { PortalConfigContext } from 'src/context';
import { FlagsContext } from 'src/context/flagsContext';
import { Contract } from 'src/entities/Contract';
import { getInitials } from 'src/utils/UserUtils';

type ContractDataWithRecipient = Contract & {
  recipientData: {
    client?: UserFields;
    company?: CompanyFields;
  };
};
type Props = {
  data: ContractDataWithRecipient;
};

const NewContractClientRenderer = ({ data }: Props) => {
  const portalConfig = React.useContext(PortalConfigContext);
  const { EnableLegacyAvatarColorMapping } = React.useContext(FlagsContext);
  const areCompaniesEnabled = !portalConfig?.features.disableCompanies;

  const { recipientData } = data;
  if (!recipientData) {
    return null;
  }

  // If we have both client and company, use UserCompanyAvatar
  if (recipientData.client && recipientData.company) {
    const shouldShowCompanyInfo =
      areCompaniesEnabled && (recipientData.client.companyIds?.length ?? 0) > 1;

    const userData = {
      name: [
        recipientData.client.givenName,
        recipientData.client.familyName,
      ].join(' '),
      src: recipientData.client.avatarImageUrl,
      fallbackColor: recipientData.client.fallbackColor,
      companyAvatar: shouldShowCompanyInfo
        ? {
            name: recipientData.company.name,
            src: recipientData.company.avatarImageURL ?? '',
            fallbackColor: recipientData.company.fallbackColor ?? '#6B7280', // fallback to gray
          }
        : undefined,
    };

    return (
      <div className="flex items-center gap-3 p-2">
        <div className="flex-none w-[34px] h-[34px] flex items-center justify-center">
          <UserWithCompanyAvatar
            size="md"
            avatarSrc={userData.src}
            fallbackColor={userData.fallbackColor}
            text={getInitials(userData.name, { twoLetters: true })}
            companyAvatarSrc={userData.companyAvatar?.src}
            companyAvatarFallbackColor={userData.companyAvatar?.fallbackColor}
            companyText={getInitials(userData.companyAvatar?.name)}
            useNewColors={EnableLegacyAvatarColorMapping}
          />
        </div>
        <div>
          <Heading size="xs">
            {[
              recipientData.client.givenName,
              recipientData.client.familyName,
            ].join(' ')}
            {shouldShowCompanyInfo && (
              <span className="text-gray-500">
                {' '}
                - {recipientData.company.name}
              </span>
            )}
          </Heading>
          <div className="text-xs text-gray-500">
            {recipientData.client.email}
          </div>
        </div>
      </div>
    );
  }

  // If we only have one (client or company), use single Avatar
  const singleEntity = recipientData.client || recipientData.company;
  if (!singleEntity) return null;

  return (
    <div className="flex items-center gap-3 p-2">
      <div className="flex-none w-[34px] h-[34px] flex items-center justify-center">
        <Avatar
          alt={
            recipientData.client
              ? [
                  recipientData.client.givenName,
                  recipientData.client.familyName,
                ].join(' ')
              : recipientData.company?.name
          }
          src={
            recipientData.client
              ? recipientData.client.avatarImageUrl
              : (recipientData.company!.avatarImageURL ?? '')
          }
          fallbackColor={recipientData.client?.fallbackColor}
          variant={recipientData.client ? 'circle' : 'rounded'}
          size="md"
          text={
            recipientData.client
              ? getInitials(recipientData.client.givenName, {
                  twoLetters: true,
                })
              : getInitials(recipientData.company!.name)
          }
          useNewColors={EnableLegacyAvatarColorMapping}
        />
      </div>
      <div>
        <Heading size="xs">
          {recipientData.client
            ? [
                recipientData.client.givenName,
                recipientData.client.familyName,
              ].join(' ')
            : recipientData.company?.name}
        </Heading>
        {recipientData.client && (
          <div className="text-xs text-gray-500">
            {recipientData.client.email}
          </div>
        )}
      </div>
    </div>
  );
};

export const ContractClientRenderer = ({ data }: Props) => {
  return <NewContractClientRenderer data={data} />;
};
